import { getApiUrl } from '../../config/api';

const BOOKING_ACTION_FETCH_TIMEOUT_MS = 20000;

async function fetchWithTimeout(url, options = {}, timeoutMs = BOOKING_ACTION_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      const timeoutError = new Error(
        'Loading interview details timed out. You can still choose custom times below.'
      );
      timeoutError.code = 'timeout';
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function parseJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    const error = new Error(payload?.error || `Request failed (${response.status})`);
    error.code = payload?.code;
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  return payload;
}

export async function fetchBookingActionContext(token, requestedAction = '') {
  const safeToken = String(token || '').trim();
  const safeAction = String(requestedAction || '').trim().toLowerCase();
  const query = new URLSearchParams({ token: safeToken });
  if (safeAction) {
    query.set('action', safeAction);
  }
  const response = await fetchWithTimeout(
    getApiUrl(`/api/meeting/booking/action/context?${query.toString()}`),
    { credentials: 'include' }
  );
  return parseJsonResponse(response);
}

export async function fetchCohostCancelContext(token) {
  const query = new URLSearchParams({ token: String(token || '').trim() });
  const response = await fetchWithTimeout(
    getApiUrl(`/api/meeting/booking/cohost-cancel/context?${query.toString()}`),
    { credentials: 'omit' }
  );
  return parseJsonResponse(response);
}

export async function submitCohostCancel({ token, reason }) {
  const response = await fetchWithTimeout(
    getApiUrl('/api/meeting/booking/cohost-cancel'),
    {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: String(token || '').trim(),
        reason: String(reason || '').trim(),
      }),
    },
    30000
  );
  return parseJsonResponse(response);
}

export async function submitBookingActionToken({
  token,
  action,
  slotValue = '',
  availabilityNotes = '',
  suggestedSlots = [],
  declineReason = '',
}) {
  const response = await fetchWithTimeout(
    getApiUrl('/api/meeting/booking/action/token'),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: String(token || '').trim(),
        action: String(action || '').trim().toLowerCase(),
        slot_value: String(slotValue || '').trim() || undefined,
        availability_notes: String(availabilityNotes || '').trim() || undefined,
        decline_reason: String(declineReason || '').trim() || undefined,
        suggested_slots: Array.isArray(suggestedSlots) && suggestedSlots.length
          ? suggestedSlots
          : undefined,
      }),
    },
    30000
  );
  return parseJsonResponse(response);
}
